import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyBrand } from "@/lib/site";
import { kes, isSunday, shiftLabel, shiftStartISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, KeyRound } from "lucide-react";
import { isAccountant as canAccount, isManager as canManage, isAdmin as canAdmin, roleLabel } from "@/lib/roles";
import SectionNav from "@/components/SectionNav";

// payout = cash paid out by the attendant (not in the cash box); unpaid = attendant shortfall still
// owed; company = drawn on the operating company's own account. All count as non-cash collections.
const FUEL_KINDS = ["cash", "mobile_money", "card", "app", "invoice", "coupon", "payout", "unpaid", "company", "other"];

export default function Config() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [prices, setPrices] = useState([]);
  const [section, setSection] = useState("station");
  const canEdit = canManage(user) || canAccount(user);

  const load = async () => {
    const [{ data }, pr] = await Promise.all([api.get("/config"), api.get("/config/fuel-prices")]);
    setCfg(data);
    setPrices(pr.data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!cfg) return <div className="py-20 text-center text-muted-foreground">Loading configuration…</div>;

  const productName = (id) => cfg.products.find((p) => p.id === id)?.name || "—";
  const tankLabel = (id) => cfg.tanks.find((t) => t.id === id)?.label || "—";

  const create = async (coll, body) => {
    try {
      await api.post(`/config/${coll}`, body);
      toast.success("Added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const update = async (coll, id, body) => {
    try {
      await api.put(`/config/${coll}/${id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const retire = async (coll, id) => {
    try {
      await api.delete(`/config/${coll}/${id}`);
      toast.success("Retired");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Station Setup</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure pumps, tanks, products, non-fuel items, payment channels &amp; prices</p>
      </div>

      <Tabs value={section} onValueChange={setSection}>
        <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
          <SectionNav label="Setup sections" groups={setupGroups(user)} value={section} onChange={setSection} />
          <div className="min-w-0">

        {/* Products */}
        <TabsContent value="products" className="mt-0">
          <SimpleCrud
            title="Fuel Products"
            items={cfg.products}
            columns={[{ k: "name", label: "Name" }, { k: "unit", label: "Unit" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("products", id)}
            addFields={[{ k: "name", label: "Name", required: true }, { k: "unit", label: "Unit", default: "L" }]}
            onAdd={(b) => create("products", { ...b, kind: "fuel" })}
          />
        </TabsContent>

        {/* Tanks */}
        <TabsContent value="tanks" className="mt-0">
          <SimpleCrud
            title="Underground Tanks"
            items={cfg.tanks.map((t) => ({ ...t, product: productName(t.product_id) }))}
            columns={[{ k: "label", label: "Tank" }, { k: "product", label: "Product" }, { k: "capacity", label: "Capacity (L)" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("tanks", id)}
            addFields={[
              { k: "label", label: "Label", required: true },
              { k: "product_id", label: "Product", type: "select", options: cfg.products.map((p) => ({ value: p.id, label: p.name })) },
              { k: "capacity", label: "Capacity (L)", type: "number" },
            ]}
            onAdd={(b) => create("tanks", b)}
          />
        </TabsContent>

        {/* Pumps */}
        <TabsContent value="pumps" className="mt-0">
          <SimpleCrud
            title="Pumps by Island"
            items={cfg.pumps.map((p) => ({ ...p, product: productName(p.product_id), tank: tankLabel(p.tank_id) }))}
            columns={[{ k: "island", label: "Island" }, { k: "label", label: "Pump" }, { k: "product", label: "Product" }, { k: "tank", label: "Tank" }, { k: "attendant", label: "Attendant" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("pumps", id)}
            addFields={[
              { k: "island", label: "Island", required: true },
              { k: "label", label: "Pump Label", required: true },
              { k: "product_id", label: "Product", type: "select", options: cfg.products.map((p) => ({ value: p.id, label: p.name })) },
              { k: "tank_id", label: "Tank", type: "select", options: cfg.tanks.map((t) => ({ value: t.id, label: t.label })) },
              { k: "attendant", label: "Attendant" },
            ]}
            onAdd={(b) => create("pumps", b)}
          />
        </TabsContent>

        {/* Non-fuel */}
        <TabsContent value="nonfuel" className="mt-0">
          <SimpleCrud
            title="Lubes, LPG & Accessories"
            items={cfg.nonfuel_items}
            columns={[{ k: "category", label: "Category" }, { k: "name", label: "Item", editable: true },
              { k: "unit_price", label: "Unit Price", money: true, editable: canAccount(user) }, { k: "cost_price", label: "Cost Price", money: true, editable: canAccount(user) }]}
            canEdit={canEdit}
            onRetire={(id) => retire("nonfuel_items", id)}
            onUpdate={(id, body) => update("nonfuel_items", id, Object.fromEntries(Object.entries(body).filter(([k, v]) => k !== "name" || v).map(([k, v]) => [k, k === "name" ? v : Number(v) || 0])))}
            addFields={[
              { k: "category", label: "Category", type: "select", options: [{ value: "lube", label: "Lube" }, { value: "lpg", label: "LPG" }, { value: "accessory", label: "Accessory" }, ...(cfg.station?.spares_store ? [{ value: "spare", label: "Spare (sold from the store)" }, { value: "service", label: "Service (price entered on the shift)" }] : [])] },
              { k: "name", label: "Name", required: true },
              ...(canAccount(user) ? [{ k: "unit_price", label: "Unit Price (KES)", type: "number" }, { k: "cost_price", label: "Cost Price (KES)", type: "number" }] : []),
            ]}
            onAdd={(b) => create("nonfuel_items", b)}
          />
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments" className="mt-0">
          <SimpleCrud
            title="Payment Channels"
            items={cfg.payment_methods}
            columns={[{ k: "name", label: "Method" }, { k: "kind", label: "Type" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("payment_methods", id)}
            addFields={[
              { k: "name", label: "Name", required: true },
              { k: "kind", label: "Type", type: "select", options: FUEL_KINDS.map((k) => ({ value: k, label: k })) },
            ]}
            onAdd={(b) => create("payment_methods", b)}
          />
        </TabsContent>

        {/* Attendants */}
        <TabsContent value="attendants" className="mt-0">
          <SimpleCrud
            title="Pump Attendants"
            items={cfg.attendants || []}
            columns={[{ k: "name", label: "Name" }, { k: "phone", label: "Phone" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("attendants", id)}
            addFields={[{ k: "name", label: "Name", required: true }, { k: "phone", label: "Phone" }]}
            onAdd={(b) => create("attendants", b)}
          />
        </TabsContent>

        {/* M-Pesa Tills */}
        {/* Debtors: credit customers (picked on each shift's invoice lines) and accounts fed by a
            whole payment column (Montrose, Visa, Shell Card) — balances under Accounting */}
        <TabsContent value="accounts" className="mt-0">
          <SimpleCrud
            title="Customers & Accounts (debtors)"
            items={(cfg.receivable_accounts || []).map((a) => ({
              ...a, method_name: cfg.payment_methods.find((m) => m.id === a.method_id)?.name || "— (customer invoices)",
            }))}
            columns={[
              { k: "name", label: "Name" }, { k: "kind", label: "Type" }, { k: "method_name", label: "Takes the whole column" },
              { k: "phone", label: "Phone", editable: true },
              { k: "credit_limit", label: "Credit limit (KES)", editable: true, placeholder: "0 = none" },
              { k: "opening_balance", label: "Opening balance owed (KES)", editable: true, placeholder: "0" },
            ]}
            canEdit={canEdit}
            onRetire={(id) => retire("receivable_accounts", id)}
            onUpdate={(id, body) => update("receivable_accounts", id, body)}
            addFields={[
              { k: "name", label: "Customer name", required: true },
              { k: "kind", label: "Type", type: "select", default: "customer", options: [{ value: "customer", label: "Credit customer" }, { value: "card", label: "Card scheme" }] },
              { k: "phone", label: "Phone" },
              { k: "credit_limit", label: "Credit limit (KES, 0 = none)", type: "number" },
              { k: "opening_balance", label: "Opening balance owed today (KES)", type: "number" },
            ]}
            onAdd={(b) => create("receivable_accounts", { kind: "customer", ...b })}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Credit customers appear in each shift&apos;s Invoices list. Montrose, Visa and Shell Card take their whole attendant-sheet column automatically.
            Balances, settlements and statements are under Accounting → Debtors &amp; Settlements.
          </p>
        </TabsContent>

        <TabsContent value="tills" className="mt-0 space-y-4">
          <SimpleCrud
            title="M-Pesa Tills"
            items={cfg.tills || []}
            columns={[
              { k: "label", label: "Label" }, { k: "till_number", label: "Till Number" },
              // Safaricom reports payments by store number — this is how a payment is matched to its till
              { k: "store_number", label: "Store Number (Safaricom)", editable: true, placeholder: "from M-PESA Org Portal" },
            ]}
            canEdit={canEdit}
            onRetire={(id) => retire("tills", id)}
            onUpdate={(id, body) => update("tills", id, body)}
            addFields={[{ k: "label", label: "Label (e.g. Till 1)", required: true }, { k: "till_number", label: "Till Number", required: true }]}
            onAdd={(b) => create("tills", b)}
          />
          {canAdmin(user) && <MpesaFeedTab tills={cfg.tills || []} isAdmin={canAdmin(user)} />}
        </TabsContent>

        {/* Fuel prices */}
        <TabsContent value="prices" className="mt-0">
          <FuelPrices cfg={cfg} prices={prices} canEdit={canEdit} reload={load} />
        </TabsContent>

        {/* Expense categories — for Section E per-shift expenses */}
        <TabsContent value="expense-categories" className="mt-0">
          <SimpleCrud
            title="Expense Categories"
            items={cfg.expense_categories || []}
            columns={[{ k: "label", label: "Label" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("expense_categories", id)}
            addFields={[{ k: "label", label: "Category Label (e.g. Utilities)", required: true }]}
            onAdd={(b) => create("expense_categories", b)}
          />
        </TabsContent>

        {/* Station tolerances */}
        <TabsContent value="station" className="mt-0">
          <StationSettings station={cfg.station} canEdit={canEdit} reload={load} />
        </TabsContent>

        {/* User accounts */}
        <TabsContent value="users" className="mt-0">
          {canAdmin(user) && <UsersTab />}
        </TabsContent>

          </div>
        </div>
      </Tabs>
    </div>
  );
}

// Head-office M-Pesa feed: Luminara's head office sends every site's payments here; this station
// keeps its own tills' payments, passes other sites' on, and shows which store numbers are paying in.
function MpesaFeedTab({ tills, isAdmin }) {
  const [feed, setFeed] = useState(null);
  const [assign, setAssign] = useState({});
  const [route, setRoute] = useState({ shortcode: "", url: "", label: "" });
  const load = useCallback(async () => {
    try { setFeed((await api.get("/mpesa/feed")).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => {
    try {
      const r = await fn();
      toast.success(typeof ok === "function" ? ok(r.data) : ok);
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };
  if (!feed) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;
  const own = Object.entries(feed.station_codes);
  const when = (v) => String(v || "").slice(0, 16).replace("T", " ");
  return (
    <div className="mt-4 grid max-w-3xl gap-4">
      <Card className="p-5 text-sm">
        <h3 className="font-display font-semibold text-slate-800">Payments from Safaricom</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Once a till is registered with Safaricom, every payment into it arrives here and is matched to the shift automatically.
          Payments for a store number that isn&apos;t this station&apos;s are passed on to that site&apos;s own system if one is set up;
          otherwise only the amount, time and store number (no name or phone) are kept for {feed.keep_days} days. Customers&apos;
          payments never depend on this server.
        </p>
        <div className="mt-3 grid gap-1 text-xs">
          <div>Mode: <b>{feed.env}</b></div>
          <div>This station&apos;s numbers: {own.length ? own.map(([c, l]) => `${l} ${c}`).join(" · ") : "none set"}</div>
          <div>Last payment to this station: {feed.last_station_payment
            ? `${when(feed.last_station_payment.at)} — KSh ${kes(feed.last_station_payment.amount)} (${feed.last_station_payment.till})`
            : "none yet"}</div>
        </div>
      </Card>

      <Card className="p-5 text-sm">
        <h3 className="font-display font-semibold text-slate-800">Safaricom connection</h3>
        <div className="mt-2 grid gap-1 text-xs">
          <div>Shortcode in the settings: <b className="font-mono">{feed.settings_shortcode}</b>{" "}
            {feed.settings_shortcode_is_station_till
              ? <span className="text-emerald-700">(this station&apos;s {feed.station_codes[feed.settings_shortcode]})</span>
              : <span className="text-amber-700">(not one of this station&apos;s tills - can&apos;t be registered from here)</span>}</div>
          {feed.registrations.map((r, i) => (
            <div key={i} className={r.status < 400 ? "text-emerald-700" : "text-rose-600"}>
              {when(r.at)} — registering {r.shortcode}: {r.status < 400 ? "accepted" : "refused"}
              {" "}({(r.safaricom && (r.safaricom.ResponseDescription || r.safaricom.errorMessage)) || r.status})
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => act(() => api.post("/mpesa/check-keys"), (d) => `Safaricom accepted the keys (${d.env}, ${d.shortcode})`)}>Check keys</Button>
          {isAdmin && feed.settings_shortcode_is_station_till && (
            <Button size="sm" className="bg-brand hover:bg-brand/90"
              onClick={() => act(() => api.post("/mpesa/register-urls"), (d) => `Safaricom registered ${d.registered}: ${d.safaricom?.ResponseDescription || "OK"}`)}>
              Register {feed.settings_shortcode} with Safaricom
            </Button>
          )}
        </div>
        {isAdmin && <p className="mt-2 text-xs text-muted-foreground">Registering tells Safaricom to send this till&apos;s payment notifications here. In production it can&apos;t simply be changed afterwards, so do it once the keys check out.</p>}
        {isAdmin && <MpesaLinks />}
        {isAdmin && <MpesaKeysForm onSaved={load} />}
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-5 py-3">
          <h3 className="font-display font-semibold text-slate-800">Store numbers paying in that aren&apos;t this station&apos;s</h3>
          <p className="text-xs text-muted-foreground">If one of these is one of our tills (for example after a test payment of a known amount), assign it. Its recent payments move to that till.</p>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Store / till no.</TableHead><TableHead>Payments</TableHead><TableHead>Total KSh</TableHead><TableHead>Last seen</TableHead><TableHead>Forwarded to</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {feed.unrouted.length === 0 && <TableRow><TableCell colSpan={6} className="py-4 text-center text-sm text-muted-foreground">None yet.</TableCell></TableRow>}
            {feed.unrouted.map((u) => (
              <TableRow key={u.shortcode}>
                <TableCell className="font-mono">{u.shortcode}</TableCell>
                <TableCell>{u.count}</TableCell>
                <TableCell>{kes(u.amount)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{when(u.last_seen)}</TableCell>
                <TableCell className="text-xs">{u.forwarded_to || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <select className="rounded border px-1.5 py-1 text-xs" value={assign[u.shortcode] || ""} onChange={(e) => setAssign({ ...assign, [u.shortcode]: e.target.value })}>
                      <option value="">— our till? —</option>
                      {tills.map((t) => <option key={t.id} value={t.id}>{t.label} ({t.till_number})</option>)}
                    </select>
                    <Button size="sm" variant="outline" className="h-7" disabled={!assign[u.shortcode]}
                      onClick={() => act(() => api.post(`/mpesa/feed/${u.shortcode}/assign`, { till_id: assign[u.shortcode] }),
                        (d) => `${d.message} — ${d.payments_attached} payment(s) attached`)}>Assign</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-5 py-3">
          <h3 className="font-display font-semibold text-slate-800">Forwarding to other sites</h3>
          <p className="text-xs text-muted-foreground">When another Luminara site has its own system, its payments can be passed on to it.{isAdmin ? "" : " Only an Admin can change this."}</p>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Store / till no.</TableHead><TableHead>Site</TableHead><TableHead>Address</TableHead><TableHead>Last forward</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {feed.routes.length === 0 && <TableRow><TableCell colSpan={5} className="py-4 text-center text-sm text-muted-foreground">No forwarding set up.</TableCell></TableRow>}
            {feed.routes.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono">{r.shortcode}</TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell className="max-w-[16rem] truncate text-xs">{r.url}</TableCell>
                <TableCell className="text-xs">{r.last_forward_at ? `${when(r.last_forward_at)} (${r.last_forward_status})` : "—"}</TableCell>
                <TableCell>{isAdmin && <Button size="sm" variant="ghost" className="h-7 text-rose-600" onClick={() => act(() => api.delete(`/mpesa/routes/${r.shortcode}`), "Removed")}>Remove</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {isAdmin && (
          <form className="grid gap-2 border-t p-4 sm:grid-cols-4"
            onSubmit={async (e) => { e.preventDefault(); if (await act(() => api.post("/mpesa/routes", route), (d) => d.message)) setRoute({ shortcode: "", url: "", label: "" }); }}>
            <Input id="route-code" placeholder="Store / till no." value={route.shortcode} onChange={(e) => setRoute({ ...route, shortcode: e.target.value })} required />
            <Input id="route-label" placeholder="Site name" value={route.label} onChange={(e) => setRoute({ ...route, label: e.target.value })} />
            <Input id="route-url" placeholder="https://… (the site's payment address)" value={route.url} onChange={(e) => setRoute({ ...route, url: e.target.value })} required />
            <Button type="submit" variant="outline">Add forwarding</Button>
          </form>
        )}
      </Card>
    </div>
  );
}

// Admin: the confirmation and validation URLs to paste into Safaricom's portal. They hold the secret
// token, so they're fetched only when asked for and hidden again with one click.
function MpesaLinks() {
  const [links, setLinks] = useState(null);
  const show = async () => {
    try {
      setLinks((await api.get("/mpesa/links")).data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed - select the text and copy it");
    }
  };
  if (!links) {
    return <Button size="sm" variant="outline" className="mt-3" onClick={show}>Show M-Pesa links</Button>;
  }
  const rows = [["Confirmation URL", links.confirmation_url], ["Validation URL", links.validation_url]];
  return (
    <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-800">M-Pesa links ({links.env}) - shortcode {links.shortcode}, response type {links.response_type}</span>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setLinks(null)}>Hide</Button>
      </div>
      <p className="text-amber-800">Keep these private: anyone with them can post payments into the app.</p>
      {rows.map(([label, url]) => (
        <div key={label} className="space-y-1">
          <Label className="text-xs">{label}</Label>
          <div className="flex gap-2">
            <Input readOnly value={url} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="outline" onClick={() => copy(url, label)}>Copy</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Admin: change the Daraja keys without a server login. Safaricom checks them before they're saved;
// saved keys are never shown again.
function MpesaKeysForm({ onSaved }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ env: "production", shortcode: "", consumer_key: "", consumer_secret: "" });
  const loadInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/mpesa/keys");
      setInfo(data);
      setF((x) => ({ ...x, env: data.env || x.env, shortcode: x.shortcode || data.shortcode || "" }));
    } catch { setInfo(null); }
  }, []);
  useEffect(() => { loadInfo(); }, [loadInfo]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mpesa/keys", f);
      toast.success(`Safaricom accepted the keys - saved for ${data.shortcode} (${data.env})`);
      setF({ ...f, consumer_key: "", consumer_secret: "" });
      setOpen(false);
      await loadInfo();
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          Keys: {info ? `consumer key ${info.consumer_key_set ? `set (${info.consumer_key_set} characters)` : "not set"}, secret ${info.consumer_secret_set ? "set" : "not set"}` : "…"}
          {info?.last_change && ` · last changed ${String(info.last_change.at).slice(0, 16).replace("T", " ")} by ${info.last_change.by}`}
        </span>
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)}>{open ? "Cancel" : "Change M-Pesa keys"}</Button>
      </div>
      {open && (
        <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="mk-env">Environment</Label>
            <select id="mk-env" className="h-9 w-full rounded-md border px-2 text-sm" value={f.env} onChange={(e) => setF({ ...f, env: e.target.value })}>
              <option value="production">Production (real payments)</option>
              <option value="sandbox">Sandbox (test)</option>
            </select></div>
          <div className="space-y-1.5"><Label htmlFor="mk-code">Shortcode (store number)</Label>
            <Input id="mk-code" inputMode="numeric" value={f.shortcode} onChange={(e) => setF({ ...f, shortcode: e.target.value.trim() })} required /></div>
          <div className="space-y-1.5"><Label htmlFor="mk-key">Consumer Key</Label>
            <Input id="mk-key" type="password" autoComplete="off" value={f.consumer_key} onChange={(e) => setF({ ...f, consumer_key: e.target.value.trim() })} required /></div>
          <div className="space-y-1.5"><Label htmlFor="mk-secret">Consumer Secret</Label>
            <Input id="mk-secret" type="password" autoComplete="off" value={f.consumer_secret} onChange={(e) => setF({ ...f, consumer_secret: e.target.value.trim() })} required /></div>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand/90">{busy ? "Checking with Safaricom…" : "Test with Safaricom and save"}</Button>
            <span className="text-xs text-muted-foreground">Copy them with the copy buttons in Daraja (My Apps → the app for this store number). They&apos;re only saved if Safaricom accepts them.</span>
          </div>
        </form>
      )}
    </div>
  );
}

function AddDialog({ title, fields, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const submit = async () => {
    await onAdd(form);
    setForm({});
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-brand/90" data-testid="config-add-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{`Add — ${title}`}</DialogTitle><DialogDescription>Fill in the fields and save.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          {fields.map((f) => (
            <div key={f.k} className="space-y-1.5">
              <Label>{f.label}</Label>
              {f.type === "select" ? (
                <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                  <SelectTrigger data-testid={`config-field-${f.k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.type === "number" ? "number" : "text"}
                  value={form[f.k] ?? f.default ?? ""}
                  onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                  data-testid={`config-field-${f.k}`}
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} className="bg-brand hover:bg-brand/90" data-testid="config-add-confirm">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Columns marked `editable` render an inline input that saves (onUpdate) when it loses focus.
function SimpleCrud({ title, items, columns, addFields, onAdd, onRetire, onUpdate, canEdit }) {
  return (
    <Card className="mt-4 overflow-hidden">
      <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-3">
        <h3 className="font-display font-semibold text-slate-800">{title}</h3>
        {canEdit && <AddDialog title={title} fields={addFields} onAdd={onAdd} />}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c.k}>{c.label}</TableHead>)}
            <TableHead className="text-right">Status</TableHead>
            {canEdit && <TableHead className="w-12" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} className={it.active === false ? "opacity-50" : ""} data-testid={`config-row-${it.id}`}>
              {columns.map((c) => (
                <TableCell key={c.k} className={c.money ? "font-mono-fin" : ""}>
                  {c.editable && canEdit && onUpdate ? (
                    <Input
                      className="h-8 max-w-[12rem]" defaultValue={it[c.k] ?? ""} placeholder={c.placeholder}
                      data-testid={`config-edit-${c.k}-${it.id}`}
                      onBlur={(e) => { if (e.target.value !== (it[c.k] ?? "")) onUpdate(it.id, { [c.k]: e.target.value.trim() }); }}
                    />
                  ) : c.money ? kes(it[c.k]) : (it[c.k] ?? "—")}
                </TableCell>
              ))}
              <TableCell className="text-right">
                {it.active === false ? <Badge variant="secondary">Retired</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>}
              </TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  {it.active !== false && (
                    <Button variant="ghost" size="icon" onClick={() => onRetire(it.id)} data-testid={`config-retire-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function FuelPrices({ cfg, prices, canEdit, reload }) {
  const { user } = useAuth();
  const isAccountant = canAccount(user);
  const [productId, setProductId] = useState(cfg.products[0]?.id || "");
  const [price, setPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  // which shift the new price starts with ("" = straight away)
  const [fromDate, setFromDate] = useState("");
  const [fromShift, setFromShift] = useState("PM");
  const add = async () => {
    try {
      await api.post("/config/fuel-prices", {
        product_id: productId, unit_price: Number(price),
        ...(fromDate ? { effective_from: shiftStartISO(fromDate, fromShift, cfg.station?.shift_hours) } : {}),
        ...(isAccountant && costPrice !== "" ? { cost_price: Number(costPrice) } : {}),
      });
      toast.success("Price version recorded");
      setPrice("");
      setCostPrice("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Card className="mt-4 overflow-hidden">
      <div className="border-b bg-slate-50 px-4 py-3">
        <h3 className="font-display font-semibold text-slate-800">Versioned Fuel Prices</h3>
        <p className="text-xs text-muted-foreground">Prices are versioned — new prices are added, never overwritten. Each shift uses the price in force when it started.</p>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 border-b bg-white px-4 py-3">
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-48" data-testid="price-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cfg.products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Selling Price / Litre (KES)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="w-40" data-testid="price-value-input" />
          </div>
          {isAccountant && (
            <div className="space-y-1.5">
              <Label>Cost Price / Litre (KES)</Label>
              <Input type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="w-40" data-testid="price-cost-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Starts from shift</Label>
            <div className="flex gap-2">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="price-from-date" />
              <select className="h-10 rounded-md border bg-white px-2 text-sm" value={fromShift} onChange={(e) => setFromShift(e.target.value)} data-testid="price-from-shift">
                {!(fromDate && isSunday(fromDate)) && <option value="AM">8AM shift (night ending 8am)</option>}
                <option value="PM">{fromDate && isSunday(fromDate) ? "Sun 1PM shift (Sat 4pm – Sun 1pm)" : "4PM shift (8am – 4pm)"}</option>
              </select>
            </div>
            <p className="text-[11px] text-muted-foreground">{fromDate ? `From the ${fromDate} ${shiftLabel(fromShift, fromDate)} shift onwards` : "Leave empty: from now (the next shift to start)"}</p>
          </div>
          <Button onClick={add} className="bg-brand hover:bg-brand/90" data-testid="price-add-button"><Save className="mr-1.5 h-4 w-4" />Record</Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead><TableHead className="text-right">Selling Price/L</TableHead>
            {isAccountant && <TableHead className="text-right">Cost Price/L</TableHead>}
            <TableHead>Effective</TableHead><TableHead>By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{cfg.products.find((x) => x.id === p.product_id)?.name || "—"}</TableCell>
              <TableCell className="text-right font-mono-fin">{kes(p.unit_price)}</TableCell>
              {isAccountant && <TableCell className="text-right font-mono-fin">{p.cost_price != null ? kes(p.cost_price) : "—"}</TableCell>}
              <TableCell className="text-xs text-muted-foreground">{p.effective_from ? new Date(p.effective_from).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Nairobi" }) : "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.created_by}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// The company details printed under the name on customer statements
function Letterhead({ station, reload }) {
  const keys = ["letterhead_address", "letterhead_phone", "letterhead_email", "letterhead_pin"];
  const [form, setForm] = useState(Object.fromEntries(keys.map((k) => [k, station[k] || ""])));
  const save = async () => {
    try {
      await api.put("/config/station", form);
      toast.success("Letterhead saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const field = (k, label, placeholder) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[k]} placeholder={placeholder} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`lh-${k}`} />
    </div>
  );
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Letterhead (customer statements)</h3>
      <p className="mt-1 text-xs text-muted-foreground">Printed under {station.operator || "the company name"} on statements. Leave blank what you don&apos;t want shown.</p>
      <div className="mt-4 grid gap-3">
        {field("letterhead_address", "Postal / physical address", "e.g. P.O. Box 12345-00100, Nairobi")}
        <div className="grid grid-cols-2 gap-3">
          {field("letterhead_phone", "Phone", "e.g. 0722 000 000")}
          {field("letterhead_email", "Email", "e.g. accounts@…")}
        </div>
        {field("letterhead_pin", "KRA PIN", "e.g. P051234567X")}
        <Button onClick={save} className="w-fit bg-brand hover:bg-brand/90" data-testid="lh-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
      </div>
    </Card>
  );
}

// Each site has its own colour, so it's obvious at a glance which site you're entering shifts into
function SiteColour({ station, reload }) {
  const [colour, setColour] = useState(station.brand_color || "#0E1E38");
  const save = async () => {
    try {
      await api.put("/config/station", { brand_color: colour });
      applyBrand(colour);
      toast.success("Site colour saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Site colour</h3>
      <p className="mt-1 text-xs text-muted-foreground">The top bar and buttons use this colour, so each site looks different. Only the Admin can change it.</p>
      <div className="mt-4 flex items-center gap-3">
        <input type="color" value={colour} onChange={(e) => setColour(e.target.value.toUpperCase())} className="h-10 w-14 cursor-pointer rounded border" data-testid="site-colour-input" />
        <span className="font-mono text-sm">{colour}</span>
        <Button onClick={save} className="bg-brand hover:bg-brand/90" data-testid="site-colour-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
      </div>
    </Card>
  );
}

function StationSettings({ station, canEdit, reload }) {
  const { user } = useAuth();
  const isAccountant = canAccount(user);
  const [form, setForm] = useState({
    cash_tolerance: station.cash_tolerance,
    throughput_warn_pct: station.throughput_warn_pct,
    throughput_crit_pct: station.throughput_crit_pct,
    cash_holding_alert_threshold: station.cash_holding_alert_threshold ?? 500000,
    pump_kes_totalizer: station.pump_kes_totalizer !== false,
  });
  const [cardFrom, setCardFrom] = useState(station.card_itemize_from || "");
  const saveCardFrom = async () => {
    try {
      await api.put("/config/station", { card_itemize_from: cardFrom || "" });
      toast.success(cardFrom ? `Visa & Shell Card itemization required from ${cardFrom}` : "Card itemization no longer required");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const [vatForm, setVatForm] = useState({
    fuel_vat_rate: station.fuel_vat_rate ?? 8,
    lube_vat_rate: station.lube_vat_rate ?? 16,
    accessory_vat_rate: station.accessory_vat_rate ?? 16,
    lpg_vat_rate: station.lpg_vat_rate ?? 0,
  });
  const save = async () => {
    try {
      await api.put("/config/station", form);
      toast.success("Tolerances saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const saveVat = async () => {
    try {
      await api.put("/config/station", vatForm);
      toast.success("VAT rates saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="flex flex-wrap gap-6">
      <Card className="mt-4 max-w-lg p-6">
        <h3 className="font-display font-semibold text-slate-800">Variance Tolerances</h3>
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Cash reconcile tolerance (KES)</Label>
            <Input type="number" value={form.cash_tolerance} disabled={!canEdit} onChange={(e) => setForm({ ...form, cash_tolerance: Number(e.target.value) })} data-testid="tol-cash-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Throughput warning threshold (%)</Label>
            <Input type="number" step="0.1" value={form.throughput_warn_pct} disabled={!canEdit} onChange={(e) => setForm({ ...form, throughput_warn_pct: Number(e.target.value) })} data-testid="tol-warn-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Throughput critical threshold (%)</Label>
            <Input type="number" step="0.1" value={form.throughput_crit_pct} disabled={!canEdit} onChange={(e) => setForm({ ...form, throughput_crit_pct: Number(e.target.value) })} data-testid="tol-crit-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Cash holding alert threshold (KES)</Label>
            <Input type="number" value={form.cash_holding_alert_threshold} disabled={!canEdit} onChange={(e) => setForm({ ...form, cash_holding_alert_threshold: Number(e.target.value) })} data-testid="tol-holding-input" />
            <p className="text-xs text-muted-foreground">Alerts on the Banking page once un-banked cash reaches this amount.</p>
          </div>
          {canEdit && <Button onClick={save} className="bg-brand hover:bg-brand/90" data-testid="tol-save-button"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
        </div>
      </Card>

      <EmailSettings station={station} isAccountant={isAccountant} reload={reload} />

      {canAdmin(user) && <SiteColour station={station} reload={reload} />}

      {isAccountant && <Letterhead station={station} reload={reload} />}

      <Card className="mt-4 max-w-lg p-6">
        <h3 className="font-display font-semibold text-slate-800">Visa &amp; Shell Card itemization</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          From this date, a shift can&apos;t be submitted until every attendant&apos;s Visa and Shell Card totals are itemized receipt by receipt
          (Visa: date, receipt no., authorization code, amount; Shell Card: date, product, receipt no., amount). Leave empty for not required. Only the accountant can change it.
        </p>
        <div className="mt-4 flex items-end gap-3">
          <div className="space-y-1.5"><Label>Required from</Label>
            <Input type="date" value={cardFrom} disabled={!isAccountant} onChange={(e) => setCardFrom(e.target.value)} data-testid="card-itemize-from-input" /></div>
          {isAccountant && <Button onClick={saveCardFrom} className="bg-brand hover:bg-brand/90" data-testid="card-itemize-from-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
        </div>
        {station.card_itemize_from
          ? <p className="mt-2 text-xs font-medium text-emerald-700">Required for shifts from {station.card_itemize_from}.</p>
          : <p className="mt-2 text-xs text-muted-foreground">Not required yet.</p>}
      </Card>

      {isAccountant && (
        <Card className="mt-4 max-w-lg p-6">
          <h3 className="font-display font-semibold text-slate-800">VAT Rates</h3>
          <p className="mt-1 text-xs text-muted-foreground">Used by the Station P&L to strip VAT out of revenue and cost — prices in the system are VAT-inclusive.</p>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Fuel VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.fuel_vat_rate} onChange={(e) => setVatForm({ ...vatForm, fuel_vat_rate: Number(e.target.value) })} data-testid="vat-fuel-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Lube VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.lube_vat_rate} onChange={(e) => setVatForm({ ...vatForm, lube_vat_rate: Number(e.target.value) })} data-testid="vat-lube-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Accessory VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.accessory_vat_rate} onChange={(e) => setVatForm({ ...vatForm, accessory_vat_rate: Number(e.target.value) })} data-testid="vat-accessory-input" />
            </div>
            <div className="space-y-1.5">
              <Label>LPG VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.lpg_vat_rate} onChange={(e) => setVatForm({ ...vatForm, lpg_vat_rate: Number(e.target.value) })} data-testid="vat-lpg-input" />
              <p className="text-xs text-muted-foreground">LPG is VAT-exempt by default (0%).</p>
            </div>
            <Button onClick={saveVat} className="bg-brand hover:bg-brand/90" data-testid="vat-save-button"><Save className="mr-1.5 h-4 w-4" />Save</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// Who gets the automatic emails (shift summaries, monthly reports, backup alerts) — accountant only
function EmailSettings({ station, isAccountant, reload }) {
  const [text, setText] = useState((station.notify_emails || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const loadLog = useCallback(async () => {
    if (!isAccountant) return;
    try { setLog((await api.get("/config/email-log")).data); } catch { setLog([]); }
  }, [isAccountant]);
  useEffect(() => { loadLog(); }, [loadLog]);
  const emails = text.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/config/station", { notify_emails: emails });
      toast.success(emails.length ? `Emails will go to ${emails.join(", ")}` : "Emails will go to the accountant login");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/config/test-email");
      toast.success(`Test email sent to ${data.sent_to.join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      loadLog();
    }
  };
  const STATUS = { sent: "text-emerald-700", failed: "text-rose-600", not_configured: "text-amber-700" };
  const KIND = { shift_summary: "Shift summary", monthly_reports: "Monthly reports", alert: "Alert", password_reset: "Password reset", test: "Test" };
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Email notifications</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Shift summaries when a shift is submitted, last month&apos;s reports on the 1st, and backup alerts go to these addresses
        (one per line). If empty, they go to the accountant login. Only the accountant can change this.
      </p>
      <textarea className="mt-3 h-24 w-full rounded-md border p-2 text-sm" disabled={!isAccountant} value={text}
        onChange={(e) => setText(e.target.value)} placeholder={"accountant@montrose.co.ke\nmanager@montrose.co.ke"} data-testid="notify-emails-input" />
      {isAccountant && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand/90" data-testid="notify-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
          <Button variant="outline" onClick={test} disabled={busy} data-testid="notify-test">Send test email</Button>
        </div>
      )}
      {isAccountant && log && log.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-700">Recent emails</div>
          <div className="mt-1 max-h-48 overflow-y-auto text-xs">
            {log.map((e, i) => (
              <div key={i} className="flex flex-wrap gap-x-2 border-b py-1">
                <span className="text-muted-foreground">{String(e.at).slice(0, 16).replace("T", " ")}</span>
                <span className="font-medium">{KIND[e.kind] || e.kind}</span>
                <span className={STATUS[e.status] || ""}>{e.status === "not_configured" ? "email not set up" : e.status}</span>
                <span className="min-w-0 truncate text-muted-foreground" title={e.error || e.subject}>{e.error || e.subject}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// Station Setup sections, grouped; items a role can't use are left out
function setupGroups(user) {
  return [
    { label: "Station", items: [["station", "Settings & tolerances"], ["prices", "Fuel prices"]] },
    { label: "Fuel", items: [["products", "Products"], ["tanks", "Tanks"], ["pumps", "Pumps"]] },
    { label: "Lubes & LPG", items: [["nonfuel", "Items"]] },
    { label: "People", items: [["attendants", "Attendants"], canAdmin(user) && ["users", "Users"]].filter(Boolean) },
    { label: "Payments & accounts", items: [["payments", "Payment methods"], ["accounts", "Customers & accounts"], ["expense-categories", "Expense categories"]] },
    { label: "M-Pesa", items: [["tills", canAdmin(user) ? "Tills & Safaricom" : "Tills"]] },
  ].map((g) => ({ ...g, items: g.items.map(([value, label]) => ({ value, label })) }));
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "manager" });
  const [busy, setBusy] = useState(false);
  const canManageUsers = canAccount(me);
  const meAdmin = canAdmin(me);

  const load = async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(typeof okMsg === "function" ? okMsg(res?.data) : okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const addUser = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await api.post("/auth/users", form);
      setForm({ name: "", email: "", role: "manager" });
      return r;
    }, (d) => (d?.invite_sent
      ? `${d.email} added — they've been emailed a link to set their password`
      : `${d?.email} added, but the email didn't send — use "Send set-password email" once email works`));
  };
  // admins are managed only by an admin; nobody changes their own role or disables themselves
  const canTouch = (u) => canManageUsers && u.id !== me?.id && (u.role !== "admin" || meAdmin);
  const roleOptions = meAdmin ? ["admin", "manager", "accountant"] : ["manager", "accountant"];

  if (!users) return <div className="py-10 text-center text-sm text-muted-foreground">Loading users…</div>;

  return (
    <div className="mt-4 grid max-w-3xl gap-4">
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-5 py-3">
          <h3 className="font-display font-semibold text-slate-800">User Accounts</h3>
          <p className="text-xs text-muted-foreground">
            Everyone signs in with their own account. The Admin can do everything; the Station Manager runs shifts, store
            deliveries and counts; the Accountant reconciles and approves. Only the Admin manages users. Forgot a password? Use &ldquo;Forgot password&rdquo;
            on the login page, or send a set-password email from here.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`} className={u.active === false ? "opacity-60" : ""}>
                <TableCell>{u.name}{u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  {canTouch(u) ? (
                    <select className="rounded border px-2 py-1 text-sm" disabled={busy} value={u.role}
                      onChange={(e) => run(() => api.patch(`/auth/users/${u.id}`, { role: e.target.value }), `${u.name} is now ${roleLabel(e.target.value)}`)}
                      data-testid={`role-select-${u.email}`}>
                      {[...new Set([...roleOptions, u.role])].map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                    </select>
                  ) : (
                    <Badge variant="secondary">{u.role_label || roleLabel(u.role)}</Badge>
                  )}
                </TableCell>
                <TableCell className={u.active === false ? "text-rose-600" : "text-emerald-700"}>{u.active === false ? "Disabled" : "Active"}</TableCell>
                <TableCell>
                  {canTouch(u) && (
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => run(() => api.post(`/auth/users/${u.id}/invite`), `Set-password email sent to ${u.email}`)}>
                        Send set-password email
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setTarget(u)} data-testid={`reset-pw-button-${u.email}`}>
                        <KeyRound className="mr-1.5 h-3.5 w-3.5" />Reset
                      </Button>
                      <Button variant="outline" size="sm" disabled={busy} className={u.active === false ? "" : "text-rose-600"}
                        onClick={() => run(() => api.patch(`/auth/users/${u.id}`, { active: u.active === false }),
                          u.active === false ? `${u.name} can sign in again` : `${u.name} is disabled and signed out`)}
                        data-testid={`toggle-active-${u.email}`}>
                        {u.active === false ? "Enable" : "Disable"}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {canManageUsers && (
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-800">Add a user</h3>
          <p className="mt-1 text-xs text-muted-foreground">They&apos;ll get an email with a link to set their own password (valid 48 hours). Nobody else ever knows it.{!meAdmin && " Only an Admin can add another Admin."}</p>
          <form onSubmit={addUser} className="mt-3 grid gap-3 sm:grid-cols-4 sm:items-end">
            <div className="space-y-1.5"><Label htmlFor="nu-name">Name</Label>
              <Input id="nu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="nu-email">Email</Label>
              <Input id="nu-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-1.5"><Label htmlFor="nu-role">Role</Label>
              <select id="nu-role" className="h-9 w-full rounded-md border px-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                {roleOptions.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select></div>
            <div className="sm:col-span-4"><Button type="submit" disabled={busy} className="bg-brand hover:bg-brand/90" data-testid="new-user-submit">Add user and send invitation</Button></div>
          </form>
        </Card>
      )}
      <ResetOtherPasswordDialog target={target} onOpenChange={(v) => !v && setTarget(null)} />
    </div>
  );
}

function ResetOtherPasswordDialog({ target, onOpenChange }) {
  const [ownPassword, setOwnPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOwnPassword(""); setNewPassword(""); setConfirmPassword("");
    onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/admin-reset-password", { user_id: target.id, own_password: ownPassword, new_password: newPassword });
      toast.success(`Password reset for ${target.email}`);
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password for {target?.name}</DialogTitle>
          <DialogDescription>Confirm with your own password to reset {target?.email}'s password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-own-password">Your Password</Label>
            <Input id="admin-own-password" type="password" value={ownPassword} autoComplete="current-password"
              onChange={(e) => setOwnPassword(e.target.value)} required data-testid="admin-reset-own-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-new-password">New Password for {target?.email}</Label>
            <Input id="admin-new-password" type="password" value={newPassword} autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="admin-reset-new-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-confirm-password">Confirm New Password</Label>
            <Input id="admin-confirm-password" type="password" value={confirmPassword} autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="admin-reset-confirm-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="admin-reset-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reset Password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
